import { useEffect, useState } from 'react';
import { LogIn, Eye, ShieldQuestion } from 'lucide-react';
import { fetchInviteUnit, joinAsInviteGuest, type GuestSession } from '@/guest/guest-session';
import { GuestLanding } from '@/guest/guest-landing';
import { storePendingJoin } from './pending-join';
import vtakLogo from '@/assets/vtak-logo.png';

/**
 * One code, two doors. Reached pre-login from a scanned/opened unit-invite link
 * (`?join=<code>`): names the unit, then lets the visitor either sign in to join
 * as a member or continue as a no-login, view-only guest. The guest path hands
 * off to the shared GuestLanding once a session is created.
 */
export function UnifiedJoin({ code }: { code: string }): React.JSX.Element {
  const [unitName, setUnitName] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [guest, setGuest] = useState<GuestSession | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void fetchInviteUnit(code).then((r) => (r ? setUnitName(r.unitName) : setInvalid(true)));
  }, [code]);

  function joinAsMember(): void {
    storePendingJoin(code);
    // Drop ?join= and reload into the authed app; Keycloak login runs, then the
    // workspace gate redeems the stored code.
    window.location.assign('/');
  }

  async function continueAsGuest(): Promise<void> {
    setBusy(true);
    const session = await joinAsInviteGuest(code, 'Guest');
    setBusy(false);
    if (session) setGuest(session);
    else setInvalid(true);
  }

  if (guest) return <GuestLanding joinCode={guest.joinCode} join={joinAsInviteGuest} />;

  return (
    <div className="fixed inset-0 z-[2000] grid place-items-center bg-[#0d0d0d] p-4">
      <div className="w-full max-w-sm rounded-xl border border-echo-border bg-[#1a1a1a] p-6 text-center shadow-2xl">
        <img src={vtakLogo} alt="VTAK" className="mx-auto mb-4 h-11 w-auto" />
        {invalid ? (
          <>
            <ShieldQuestion className="mx-auto mb-3 h-7 w-7 text-red-400" />
            <h1 className="text-base font-semibold text-foreground">Invite not valid</h1>
            <p className="mx-auto mt-2 max-w-xs text-xs text-muted-foreground">
              This join code is invalid, expired, or was revoked. Ask for a new one.
            </p>
          </>
        ) : (
          <>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">You’re invited to</p>
            <h1 className="mt-1 text-lg font-semibold text-foreground">{unitName ?? '…'}</h1>
            <p className="mx-auto mt-2 max-w-xs text-xs leading-relaxed text-muted-foreground">
              Join as a member, or continue as a guest to view the mission area — no account needed.
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={joinAsMember}
                className="flex items-center justify-center gap-2 rounded-md bg-echo-yellow px-3 py-2.5 text-sm font-semibold text-black transition-opacity hover:opacity-90"
              >
                <LogIn className="h-4 w-4" /> Sign in to join as member
              </button>
              <button
                type="button"
                disabled={busy || !unitName}
                onClick={() => void continueAsGuest()}
                className="flex items-center justify-center gap-2 rounded-md border border-echo-border px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-white/[0.06] disabled:opacity-50"
              >
                <Eye className="h-4 w-4" /> {busy ? 'Joining…' : 'Continue as guest (view-only)'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
